/**
 * The impersonation mapping command — the one contract `POST /api/admin/me/mapping`
 * speaks, shared so the server validates and the client builds the same shape.
 *
 * It lives here rather than in `src/api/admin.ts` because a client-side twin of
 * it is what caused the 2026-09-17 outage: the route replaced an inferred body
 * with this explicit command, four frontend call sites hand-encoded that body,
 * and the one in a mini-app was missed — every Delphi impersonation switch
 * answered `validation_failed` in production until it was found. Per the
 * project's "Schemas Are the Source of Truth" rule the TS types are `z.infer`
 * from these schemas; do not hand-write parallel interfaces. Background:
 * `docs/impersonation.md`.
 */

import { z } from 'zod';

/** Who to act as. The server resolves either identifier to the same user. */
export const ImpersonationTargetSchema = z.union([
  z.object({ email: z.string().min(1) }),
  z.object({ slackUserId: z.string().min(1) }),
]);
export type ImpersonationTarget = z.infer<typeof ImpersonationTargetSchema>;

/**
 * Start acting as someone, or stop and return to yourself.
 *
 * `action` is explicit on purpose. The route used to infer start / stop / stop-again
 * from two optional strings, which let a client-supplied identity drive the stop
 * path; targeting yourself is now `cannot_start_self`, not a silent stop.
 */
export const ImpersonationCommandSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('start'), target: ImpersonationTargetSchema }),
  z.object({ action: z.literal('stop') }),
]);
export type ImpersonationCommand = z.infer<typeof ImpersonationCommandSchema>;

/** Stop, spelled once so call sites read as prose and never build the body. */
export const STOP_IMPERSONATING: ImpersonationCommand = { action: 'stop' };
